import { apiClient } from '@/lib/api/client';
import type {
  ShippingRate,
  ShippingZone,
  CreateShippingRateData,
  UpdateShippingRateData,
  ShippingRateFilters,
} from '../types/shipping.types';

// Server response structure for list endpoints
interface ShippingRateListResponse {
  message: string;
  data: ShippingRate[];
  total: number;
  page: number;
  totalPages: number;
}

// Server response structure for single item endpoints
interface ApiData<T> {
  message?: string;
  data: T;
}

export const shippingService = {
  // Shipping Rates
  async getShippingRates(filters?: ShippingRateFilters): Promise<ShippingRate[]> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    params.set('page', String(filters?.page || 1));
    params.set('limit', String(filters?.limit || 20));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
    if (filters?.sortBy) params.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);

    const response = await apiClient.get<ShippingRateListResponse>(
      `/shipping-rates/seller?${params.toString()}`
    );
    // Server returns { message, data: ShippingRate[], total, page, totalPages }
    return response.data?.data ?? [];
  },

  async getShopShippingRates(shopRefId: string): Promise<ShippingRate[]> {
    const response = await apiClient.get<ShippingRateListResponse>(
      `/shipping-rates/shop/${shopRefId}`
    );
    return response.data?.data ?? [];
  },

  async getShippingRate(refId: string): Promise<ShippingRate | null> {
    const response = await apiClient.get<ApiData<ShippingRate>>(`/shipping-rates/${refId}`);
    return response.data?.data ?? null;
  },

  async createShippingRate(data: CreateShippingRateData): Promise<ShippingRate | null> {
    const response = await apiClient.post<ApiData<ShippingRate>>('/shipping-rates/create', data);
    return response.data?.data ?? null;
  },

  async updateShippingRate(refId: string, data: UpdateShippingRateData): Promise<ShippingRate | null> {
    const response = await apiClient.put<ApiData<ShippingRate>>(`/shipping-rates/${refId}`, data);
    return response.data?.data ?? null;
  },

  async toggleShippingRateStatus(refId: string): Promise<ShippingRate | null> {
    const response = await apiClient.put<ApiData<ShippingRate>>(
      `/shipping-rates/${refId}/toggle-status`
    );
    return response.data?.data ?? null;
  },

  async deleteShippingRate(refId: string): Promise<void> {
    await apiClient.delete('/shipping-rates/delete', {
      data: { refId },
    });
  },

  async associateRatesWithShop(shopRefId: string, rateRefIds: string[]): Promise<void> {
    await apiClient.post(`/shipping-rates/shop/${shopRefId}/associate`, {
      shippingRateRefIds: rateRefIds,
    });
  },

  async disassociateRatesFromShop(shopRefId: string, rateRefIds: string[]): Promise<void> {
    await apiClient.delete(`/shipping-rates/shop/${shopRefId}/disassociate`, {
      data: { shippingRateRefIds: rateRefIds },
    });
  },

  // Shipping Zones
  async getShippingZones(filters?: { page?: number; limit?: number; search?: string }): Promise<ShippingZone[]> {
    const params = new URLSearchParams();
    // page and limit are required by the server
    params.set('page', String(filters?.page || 1));
    params.set('limit', String(filters?.limit || 20));
    if (filters?.search) params.set('search', filters.search);

    const response = await apiClient.get<{ message?: string; data: ShippingZone[]; total: number; page: number; totalPages: number }>(
      `/shipping-zones?${params.toString()}`
    );
    // Server returns { message, data: ShippingZone[], total, page, totalPages }
    return response.data?.data ?? [];
  },

  async getShippingZone(refId: string): Promise<ShippingZone | null> {
    const response = await apiClient.get<ApiData<ShippingZone>>(`/shipping-zones/${refId}`);
    return response.data?.data ?? null;
  },

  async createShippingZone(data: Partial<ShippingZone>): Promise<ShippingZone | null> {
    const response = await apiClient.post<ApiData<ShippingZone>>('/shipping-zones/create', data);
    return response.data?.data ?? null;
  },

  async updateShippingZone(refId: string, data: Partial<ShippingZone>): Promise<ShippingZone | null> {
    const response = await apiClient.put<ApiData<ShippingZone>>(`/shipping-zones/${refId}`, data);
    return response.data?.data ?? null;
  },

  async toggleShippingZoneStatus(refId: string): Promise<ShippingZone | null> {
    const response = await apiClient.put<ApiData<ShippingZone>>(
      `/shipping-zones/${refId}/toggle-status`
    );
    return response.data?.data ?? null;
  },

  async deleteShippingZone(refId: string): Promise<void> {
    await apiClient.delete(`/shipping-zones/${refId}`);
  },
};
